import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Home, GitCompare, FileSignature, MessageSquare, Users, DollarSign, Search } from 'lucide-react';
import { useCompare } from '@/hooks/useCompare';

/* eslint-disable react/prop-types */
function NavItem({ icon: Icon, label, to, active, onClick, badge, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5 transition-colors relative ${
        active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
      }`}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
    >
      <div className="relative">
        <Icon className="w-5 h-5" />
        {badge != null && badge > 0 && (
          <span
            className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-semibold flex items-center justify-center"
            aria-label={`${badge} items`}
          >
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </div>
      <span className="text-[10px] font-medium leading-none">{label}</span>
    </button>
  );
}
/* eslint-enable react/prop-types */

/**
 * Persistent bottom nav for mobile (`md:hidden`).
 * Hidden when the user isn't logged in.
 * Adapts items per user role.
 */
export function MobileBottomNav() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { count: compareCount } = useCompare();

  if (!user) return null;

  // Certain full-screen flows suppress the bar (e.g. auth callback)
  const suppressed = ['/auth', '/auth/callback', '/reset-password'].some((p) => location.pathname.startsWith(p));
  if (suppressed) return null;

  let items;
  if (user.role === 'tenant') {
    items = [
      { icon: Search, label: 'Browse', to: '/tenant/dashboard', testId: 'bottomnav-browse' },
      { icon: GitCompare, label: 'Compare', to: '/compare', badge: compareCount, testId: 'bottomnav-compare' },
      { icon: FileSignature, label: 'Applications', to: '/leases', testId: 'bottomnav-applications' },
      { icon: MessageSquare, label: 'Messages', to: '/messages', testId: 'bottomnav-messages' },
    ];
  } else if (user.role === 'landlord' || user.role === 'employee') {
    items = [
      { icon: Home, label: 'Dashboard', to: '/landlord/dashboard', testId: 'bottomnav-dashboard' },
      { icon: FileSignature, label: 'Leases', to: '/leases', testId: 'bottomnav-leases' },
      { icon: MessageSquare, label: 'Messages', to: '/messages', testId: 'bottomnav-messages' },
      { icon: user.role === 'landlord' ? DollarSign : Users, label: user.role === 'landlord' ? 'Payouts' : 'Team', to: user.role === 'landlord' ? '/landlord/payouts' : '/landlord/employees', testId: 'bottomnav-payouts' },
    ];
  } else if (user.role === 'admin') {
    items = [
      { icon: Home, label: 'Dashboard', to: '/admin/dashboard', testId: 'bottomnav-dashboard' },
      { icon: Users, label: 'Users', to: '/admin/dashboard', testId: 'bottomnav-users' },
      { icon: MessageSquare, label: 'Messages', to: '/messages', testId: 'bottomnav-messages' },
    ];
  } else {
    return null;
  }

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-background/95 backdrop-blur border-t border-border pb-[env(safe-area-inset-bottom)]"
      aria-label="Primary"
    >
      <div className="flex items-stretch max-w-md mx-auto">
        {items.map((it) => (
          <NavItem
            key={it.to + it.label}
            icon={it.icon}
            label={it.label}
            to={it.to}
            testId={it.testId}
            badge={it.badge}
            active={location.pathname === it.to || (it.to !== '/' && location.pathname.startsWith(it.to))}
            onClick={() => navigate(it.to)}
          />
        ))}
      </div>
    </nav>
  );
}
